/**
 * `/nlsearch` returns filters as free text (`{ filterType: 'pool' }`) rather than
 * Relax's category/code pairs, so they have to be translated before they can be
 * serialized onto a Relax URL.
 */
import type { FilterCategoryHotel } from '../types'
import type { NlSearchFilter } from './aiSearch'

export type NormalizedSearchFilter = {
  filterType: FilterCategoryHotel
  filterValue: string
}

const HOTEL_FILTER_CATEGORIES = new Set<FilterCategoryHotel>([
  'amenities',
  'hotelStarLevel',
  'guestRating',
  'propertyTypes',
  'AMENITY',
  'HOTEL_STAR_LEVEL',
  'GUEST_RATING',
  'PROPERTY_TYPE',
  'ALL_BRANDS',
  'PROPERTY_THEME',
])

const AMENITY_CODE_BY_PHRASE: Record<string, string> = {
  'adults only': 'ADULTONLY',
  'airport shuttle': 'AIRSHUTTL',
  'all inclusive': 'INCLUSIVE',
  beach: 'BCHFRNT',
  beachfront: 'BCHFRNT',
  breakfast: 'FBRKFST',
  'breakfast included': 'FBRKFST',
  casino: 'CASINO',
  'complimentary breakfast': 'FBRKFST',
  'dog friendly': 'PETALLOW',
  'fitness center': 'FITSPA',
  'free breakfast': 'FBRKFST',
  'free internet': 'FINTRNT',
  'free parking': 'FPRKING',
  'free wifi': 'FINTRNT',
  gym: 'FITSPA',
  'indoor pool': 'SPOOL',
  internet: 'FINTRNT',
  kitchen: 'KITCHEN',
  kitchenette: 'KITCHEN',
  'outdoor pool': 'SPOOL',
  parking: 'FPRKING',
  'pet friendly': 'PETALLOW',
  pets: 'PETALLOW',
  'pets allowed': 'PETALLOW',
  pool: 'SPOOL',
  restaurant: 'RESTRNT',
  spa: 'SPA',
  'swimming pool': 'SPOOL',
  waterfront: 'BCHFRNT',
  wifi: 'FINTRNT',
}

const GUEST_RATING_PATTERN = /(\d+(?:\.\d)?)\s*\+?\s*(?:guest\s*)?rating/
const STAR_RATING_PATTERN = /(\d+(?:\.\d)?)\s*\+?\s*star/

function isHotelFilterCategory(value: string): value is FilterCategoryHotel {
  return HOTEL_FILTER_CATEGORIES.has(value as FilterCategoryHotel)
}

function normalizeText(value?: string | null) {
  return (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, ' ')
    .trim()
}

function resolveAmenityCode(...sources: Array<string | undefined | null>) {
  for (const source of sources) {
    const phrase = normalizeText(source)
    if (!phrase) continue
    const code = AMENITY_CODE_BY_PHRASE[phrase]
    if (code) return code
  }
  return null
}

function resolveGuestRating(...sources: Array<string | undefined | null>) {
  for (const source of sources) {
    const phrase = normalizeText(source)
    if (!phrase) continue
    const match = GUEST_RATING_PATTERN.exec(phrase)
    if (match?.[1]) return match[1]
  }
  return null
}

/**
 * `/nlsearch` may return an already-expanded list (`"4,4.5,5"` for 4+) or a bare
 * threshold (`"4"` / `"4+"`). Relax's URL builder only needs the minimum star.
 */
function resolveStarRating(...sources: Array<string | undefined | null>) {
  for (const source of sources) {
    if (!source) continue

    const trimmed = source.trim()
    if (/^[\d.]+(?:,[\d.]+)+$/.test(trimmed)) {
      const nums = trimmed
        .split(',')
        .map(Number)
        .filter((n) => Number.isFinite(n))
      if (nums.length) return String(Math.min(...nums))
    }

    const phrase = normalizeText(source)
    if (!phrase) continue
    const match = STAR_RATING_PATTERN.exec(phrase)
    if (match?.[1]) return match[1]

    const bare = /^(\d+(?:\.\d)?)\s*\+?$/.exec(phrase)
    if (bare?.[1]) return bare[1]
  }
  return null
}

function normalizeCategoryFilter(
  filterType: FilterCategoryHotel,
  filter: NlSearchFilter,
): NormalizedSearchFilter | null {
  const { filterValue, filterLabel } = filter

  if (filterType === 'amenities' || filterType === 'AMENITY') {
    const code = resolveAmenityCode(filterValue, filterLabel) ?? filterValue
    return code ? { filterType: 'amenities', filterValue: code } : null
  }

  if (filterType === 'guestRating' || filterType === 'GUEST_RATING') {
    const value = resolveGuestRating(filterValue, filterLabel) ?? filterValue
    return value ? { filterType: 'guestRating', filterValue: value } : null
  }

  if (filterType === 'hotelStarLevel' || filterType === 'HOTEL_STAR_LEVEL') {
    const value = resolveStarRating(filterValue, filterLabel) ?? filterValue
    return value ? { filterType: 'hotelStarLevel', filterValue: value } : null
  }

  return { filterType, filterValue }
}

/**
 * Translates one interpreted filter into the category/code pair Relax understands.
 * Returns `null` when the phrase has no Relax equivalent.
 */
export function normalizeApiFilter(filter: NlSearchFilter): NormalizedSearchFilter | null {
  if (!filter.filterType || !filter.filterValue) return null

  if (isHotelFilterCategory(filter.filterType)) {
    return normalizeCategoryFilter(filter.filterType, filter)
  }

  const phrases = [filter.filterValue, filter.filterType, filter.filterLabel]
    .map(normalizeText)
    .filter(Boolean)

  for (const phrase of phrases) {
    const amenityCode = AMENITY_CODE_BY_PHRASE[phrase]
    if (amenityCode) return { filterType: 'amenities', filterValue: amenityCode }

    const guestRating = GUEST_RATING_PATTERN.exec(phrase)
    if (guestRating?.[1]) return { filterType: 'guestRating', filterValue: guestRating[1] }

    const starRating = STAR_RATING_PATTERN.exec(phrase)
    if (starRating?.[1]) return { filterType: 'hotelStarLevel', filterValue: starRating[1] }
  }

  return null
}
